import { SelectModal } from 'component/Modal/SelectModal'
import { useCallback, useRef } from 'react'
import useHintModal from './useHintModal'

interface Option {
  text: string
  onSelect: () => void
}

interface OpenSelectParams {
  title?: string
  message?: string
  options?: Option[]
  onCancel?: () => void
  initialIndex?: number
}

interface UseSelectModalResult {
  openSelect: (params: OpenSelectParams) => void
}

export default function useSelectModal(): UseSelectModalResult {
  const { openHintModal, closeHintModal } = useHintModal()

  const currentCancel = useRef<(() => void) | undefined>(undefined)
  const currentOptions = useRef<Option[] | undefined>(undefined)

  const onCancelProxy = useCallback(() => {
    closeHintModal()
    currentCancel.current?.()
  }, [closeHintModal])

  const onConfirmProxy = useCallback(
    (index: number) => {
      closeHintModal()
      currentOptions.current?.[index]?.onSelect?.()
    },
    [closeHintModal]
  )

  const openSelect = useCallback(
    ({ title, message, options = [] as Option[], onCancel, initialIndex = -1 }: OpenSelectParams) => {
      currentCancel.current = onCancel
      currentOptions.current = options
      openHintModal({
        children: SelectModal,
        props: {
          title,
          message,
          options,
          onConfirm: onConfirmProxy,
          onCancel: onCancelProxy,
          initialIndex,
        },
      })
    },
    [onCancelProxy, onConfirmProxy, openHintModal]
  )

  return { openSelect }
}
